import { Component, OnInit } from '@angular/core';
import { ScreenSizeService } from '../../../../../services/utils/screenSize.service';
import { ControlPanelService } from '../../clinic-control-panel/control-panel.service';
import { ProfileService } from '../../../../auth/services/profile.service';
import { MenuItem } from '../sidebar/sidebar.component';
import { PartnerStatus } from '../../../../models/partner.model';
import { Subscription } from 'rxjs';
import { OffcanvasHistoryService } from '../../../../../services/offcanvas-history.service';
import { NavigationEnd, Router } from '@angular/router';
import { PartnerAuthGuard } from '../../../../auth/partner-authguard';

@Component({
  selector: 'app-sidemenu',
  templateUrl: './sidemenu.component.html',
  styleUrls: ['./sidemenu.component.scss'],
  standalone: false
})
export class SidemenuComponent implements OnInit {
  menuItems: MenuItem[] = [];
  private subscriptions = new Subscription();
  activeItem: any = null;

  showPartnerFooter = false;
  currentPartnerId: number | null = null;
  partnerData: any = null;
  languageFlags: { src: string; name: string }[] = [];

  constructor(
    protected screenSize: ScreenSizeService,
    protected controlPanelService: ControlPanelService,
    protected profileService: ProfileService,
    private offCanvasHistory: OffcanvasHistoryService,
    private router: Router
  ) {
    this.subscriptions.add(
      controlPanelService.countersReady.subscribe((ready: boolean) => {
        if (ready) {
          this.setMenuItemsBasedOnRole();
          this.setActiveItemFromRoute(this.router.url);
        }
      })
    );

    // M2: the menu was only ever built once from the initial profile (role/permissions/owner
    // flags baked in at construction), and it never noticed a profile reload - e.g. the interceptor
    // silently refreshing it after a PERMISSION_REQUIRED 403 (see handlePermissionDenied), or the
    // owner changing another member's permissions - so a stale menu could keep showing (or hiding)
    // entries the account no longer has (or now has) access to until a full page reload.
    this.subscriptions.add(
      this.profileService.profileChanged$.subscribe(() => {
        this.setMenuItemsBasedOnRole();
        this.disableMenuIfPartnerPending();
        this.setActiveItemFromRoute(this.router.url);
        this.initPartnerFooter();
      })
    );
  }

  async ngOnInit() {
    await this.initializeSidebar();

    this.setActiveItemFromRoute(this.router.url);

    this.subscriptions.add(
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.setActiveItemFromRoute(event.urlAfterRedirects);
        }
      })
    );
  }

  async onItemClick(item: MenuItem) {
    if (!item.enabled) return;

    this.activeItem = item;
    this.toggleItem(item);

    if (item.route) {
      if (this.screenSize.isMobile) {
        this.offCanvasHistory.dismiss(true);
      }
      await this.router.navigate([item.route]);
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  private async initializeSidebar() {
    this.setMenuItemsBasedOnRole();
    // Cancellazione account programmata: il menu è nascosto e le API del pannello sono bloccate.
    if (this.profileService.profile?.partner?.status === PartnerStatus.PENDING_DELETION) {
      return;
    }
    await this.controlPanelService.getCounters();
    this.setMenuItemsBasedOnRole();
    this.disableMenuIfPartnerPending();
    this.setActiveItemFromRoute(this.router.url);
    this.initPartnerFooter();
  }

  async refreshCounters() {
    await this.controlPanelService.getCounters();
    this.setMenuItemsBasedOnRole();
  }

  private disableMenuIfPartnerPending(): void {
    const profile = this.profileService.profile;

    if (
      profile?.role === 'PARTNER' &&
      profile?.partner?.status == PartnerStatus.PENDING
    ) {
      this.menuItems.forEach((item) => {
        item.enabled = false;
        if (item.subItems) {
          item.subItems.forEach(sub => sub.enabled = false);
        }
      });
    }
  }

  private setMenuItemsBasedOnRole(): void {
    if (this.profileService.isAdmin) {
      this.setupAdminSidebar();
    } else if (this.profileService.isSupport) {
      this.setupSupportSidebar();
    } else {
      this.setupPartnerSidebar();
    }
  }

  setupAdminSidebar() {
    this.menuItems = [
      {
        id: 'admin-dashboard',
        icon: 'dashboard',
        label: 'Dashboard',
        route: '/admin/dashboard',
        enabled: true,
      },
      {
        id: 'admin-leads',
        icon: 'briefcase',
        label: `Leads`,
        route: '/admin/leads',
        enabled: true,
      },
      {
        id: 'admin-quote-requests',
        icon: 'file-text',
        label: `Quote requests`,
        route: '/admin/quote-requests',
        enabled: true,
      },
      {
        id: 'admin-contact-requests',
        icon: 'users',
        label: `Contact requests`,
        route: '/admin/contact-requests',
        enabled: true,
      },
      {
        id: 'admin-quotes',
        icon: 'file-text',
        label: `Quotes`,
        route: '/admin/quotes',

        enabled: true,
      },
      {
        id: 'admin-clinics',
        icon: 'building-hospital',
        label: `Clinics`,
        route: '/admin/clinics',
        badgeCount: this.dashboardCounters?.clinicsToReview,
        enabled: true,
      },
      {
        id: 'admin-reviews',
        icon: 'star',
        label: `Reviews`,
        route: '/admin/reviews',
        badgeCount: this.dashboardCounters?.reviews,
        enabled: true,
      },
      {
        id: 'invoices',
        icon: 'receipt',
        label: 'Invoices',
        route: '/admin/invoices',
        enabled: true,
      },
      {
        id: 'admin-qr-codes',
        icon: 'qrcode',
        label: 'QR Codes',
        route: '/admin/qr-codes',
        enabled: true,
      },
      {
        id: 'admin-reports',
        icon: 'report',
        label: 'Reports',
        route: '/admin/reports',
        badgeCount: this.dashboardCounters?.reports,
        enabled: true,
      },
      {
        id: 'admin-sms',
        // Il set è Tabler ("ti ti-<nome>") e non contiene "sms": un nome inesistente
        // non rende alcuna icona, lasciando la voce spaiata rispetto alle altre.
        icon: 'device-mobile-message',
        label: 'SMS',
        route: '/admin/sms',
        enabled: true,
      }
    ];
  }

  setupPartnerSidebar() {
    const isPartnerApproved = PartnerAuthGuard.isPartnerApproved(this.profileService);
    const canSeeBilling = this.profileService.meets('AUTHORIZE_PAYMENTS');
    const isOwner = this.profileService.isOwner;
    this.menuItems = [
      {
        id: 'partner-dashboard',
        icon: 'dashboard',
        label: 'Dashboard',
        route: '/partners/dashboard',
        enabled: true,
      },
      {
        id: 'partner-new-contact-requests',
        icon: 'users',
        label: `Contacts management`,
        route: '/partners/new-contact-requests',
        badgeCount: this.dashboardCounters?.newContactRequests,
        enabled: isPartnerApproved,
      },
      {
        id: 'partner-new-quote-requests',
        icon: 'file-text',
        label: `Quotes management`,
        route: '/partners/new-quote-requests',
        badgeCount: this.dashboardCounters?.newQuoteRequests,
        enabled: isPartnerApproved,
      },
      {
        id: 'partner-reviews',
        icon: 'star',
        label: `Reviews`,
        route: '/partners/reviews',
        enabled: isPartnerApproved,
      },
      ...(canSeeBilling ? [{
        id: 'partner-invoices',
        icon: 'receipt',
        label: `Invoices`,
        route: '/partners/invoices',
        enabled: isPartnerApproved,
      }] : []),
      ...(isOwner ? [{
        id: 'partner-users',
        icon: 'users-group',
        label: 'Users',
        route: '/partners/users',
        enabled: isPartnerApproved,
      }] : []),
    ];
  }

  setupSupportSidebar() {
    this.menuItems = [
      {
        id: 'support-leads',
        icon: 'briefcase',
        label: 'Leads',
        route: '/support/leads',
        enabled: true,
      },
    ];
  }

  get dashboardCounters() {
    return this.controlPanelService.dashboardCounters;
  }

  toggleItem(item: any) {
    item.open = !item.open;
  }

  private setActiveItemFromRoute(currentUrl: string) {
    if (!this.menuItems || this.menuItems.length === 0) {
      return;
    }

    const cleanUrl = currentUrl.startsWith('/') ? currentUrl : '/' + currentUrl;

    const urlWithoutParams = cleanUrl.split('?')[0].split('#')[0];

    for (const item of this.menuItems) {
      if (item.subItems) {
        const sortedSubItems = [...item.subItems].sort((a, b) => b.route.length - a.route.length);
        const sub = sortedSubItems.find(sub => urlWithoutParams.startsWith(sub.route));
        if (sub) {
          this.activeItem = sub;
          item.open = true;
          return;
        }
      }
    }

    const sortedItems = [...this.menuItems].sort((a, b) => b.route.length - a.route.length);
    const found = sortedItems.find(item => urlWithoutParams.startsWith(item.route));
    if (found) {
      this.activeItem = found;
      return;
    }
  }

  private initPartnerFooter(): void {
    const partner = this.profileService.profile?.partner;
    this.currentPartnerId = partner?.id ?? null;
    this.partnerData = partner ?? null;
    this.showPartnerFooter = !!(partner?.id && partner?.status === 'APPROVED');
    this.languageFlags = this.buildLanguageFlags(partner?.languagesSpoken);
  }

  private buildLanguageFlags(languagesSpoken?: string[]): { src: string; name: string }[] {
    if (!languagesSpoken) return [];
    const languageNames: Record<string, string> = {
      'en': 'English', 'it': 'Italian', 'de': 'German',
      'fr': 'French', 'es': 'Spanish', 'al': 'Albanian',
      'gb': 'English', 'us': 'English'
    };
    return languagesSpoken
      .map((lang: any) => {
        const code = lang.toLowerCase();
        const name = languageNames[code];
        if (!name) return null;
        return { src: `https://flagcdn.com/w40/${code}.png`, name };
      })
      .filter((f: any): f is { src: string; name: string } => f !== null);
  }

}
